import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import AddUserModal from './AddUserModal';
import ErrorModal from './ErrorModal';
import { ModuleInfoButton } from './ModuleInfoButton';
import Button from './ui/Button';
import Pagination from './ui/Pagination';
import { usePagedRows } from '../hooks/usePagedRows';
import { EmptyState, ErrorState } from './ui/States';
import { useConfirm } from './ui/ConfirmProvider';
import {
  createUser,
  deleteUser,
  getUsers,
  toggleUserStatus,
  updateUser,
  User,
  CreateUserData,
  UpdateUserData,
} from '../lib/usersApi';
import { getRoles, Role } from '../lib/rolesApi';
import { useToast } from '../context/ToastContext';
import LoadingState from './LoadingState';
import StatusBadge from './StatusBadge';
import ToggleSwitch from './ToggleSwitch';
import { formatDate as fmtDate } from "../utils/dateFormat";

const USER_MANAGEMENT_INFO_CONTENT = (
  <div className="flex flex-col gap-3">
    <p>In User Management, an admin can create user roles and manage users.</p>
    <p className="font-medium">Add New User</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>An admin can add, edit, and delete users.</li>
      <li>To add a user, go to My Users, click the Add New User button, and enter the user&apos;s name, password, and role.</li>
    </ul>
    <p className="font-medium">Create Role</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>An admin can create, edit, and delete roles.</li>
      <li>To create a role, go to My Roles, click the Add New Role button, enter a role name, and assign the required permissions.</li>
    </ul>
  </div>
);

interface UserManagementProps {
  onNavigateToRoles?: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onNavigateToRoles }) => {
  const [users, setUsers] = useState<User[]>([]);
  const pagedUsers = usePagedRows(users);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const toast = useToast();
  const confirm = useConfirm();
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  // Load users and roles on component mount
  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
      setError('');
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error('Error loading users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const fetchedRoles = await getRoles();
      setRoles(fetchedRoles);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const handleAddUser = async (userData: CreateUserData) => {
    try {
      const newUser = await createUser(userData);
      setUsers([...users, newUser]);
      setIsAddModalOpen(false);
      setError('');
      toast.success('User created successfully!');
    } catch (err: any) {
      // Re-throw validation errors so the modal can handle them
      if (err.isValidationError) {
        throw err;
      }
      // Show error in modal instead of behind popup
      setErrorModal({
        isOpen: true,
        title: 'User Creation Failed',
        message: err.message || 'Failed to create user. Please try again.'
      });
      throw err; // Re-throw so modal knows there was an error
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsAddModalOpen(true);
  };

  const handleUpdateUser = async (userData: UpdateUserData) => {
    if (editingUser) {
      try {
        const updatedUser = await updateUser(editingUser.id, userData);
        setUsers(users.map(user => 
          user.id === editingUser.id ? updatedUser : user
        ));
        setEditingUser(null);
        setIsAddModalOpen(false);
        setError('');
        toast.success('User updated successfully!');
      } catch (err: any) {
        // Re-throw validation errors so the modal can handle them
        if (err.isValidationError) {
          throw err;
        }
        // Show error in modal instead of behind popup
        setErrorModal({
          isOpen: true,
          title: 'User Update Failed',
          message: err.message || 'Failed to update user. Please try again.'
        });
        throw err; // Re-throw so modal knows there was an error
      }
    }
  };

  // The confirmations below used to be two ConfirmationModal instances driven by their own
  // open/target state. useConfirm is the app-wide dialog, so the question, the Escape
  // behaviour and the red confirm button now match every other destructive action.
  // The list updates go through the functional form of setUsers because an await now spans
  // a user decision, and the `users` captured when the row was clicked can be stale by then.
  const handleDeleteUser = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    const confirmed = await confirm({
      title: 'Are you sure?',
      summary: 'Deleting this user is permanent. You will not be able to restore it later.',
      tone: 'error',
      confirmLabel: 'Delete'
    });
    if (!confirmed) return;

    try {
      await deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setError('');
      toast.success('User deleted successfully!');
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        title: 'User Deletion Failed',
        message: err.message || 'Failed to delete user. Please try again.'
      });
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const nextActive = !user.isActive;

    const confirmed = await confirm({
      title: 'Are you sure?',
      summary: nextActive
        ? 'This user account will be restored with all previous permissions.'
        : 'This user will not be able to log in or access the system until reactivated.',
      // Locking someone out is the destructive direction; letting them back in is not.
      tone: nextActive ? 'confirm' : 'error',
      confirmLabel: nextActive ? 'Activate' : 'Deactivate'
    });
    if (!confirmed) return;

    try {
      const updatedUser = await toggleUserStatus(userId, nextActive);
      setUsers(prev => prev.map(u => (u.id === userId ? updatedUser : u)));
      setError('');
      toast.success(nextActive ? 'User activated successfully!' : 'User deactivated successfully!');
    } catch (err: any) {
      setErrorModal({
        isOpen: true,
        title: nextActive ? 'Activation Failed' : 'Deactivation Failed',
        message: err.message || 'Failed to update user status. Please try again.'
      });
    }
  };

  const getRoleDisplayName = (role: string) => {
    const roleNames: { [key: string]: string } = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'quotation_staff': 'Quotation Staff',
      'viewer': 'Viewer',
      'user': 'User'
    };
    return roleNames[role] || role;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getRoleBadgeColor = (role: string) => {
    const colors: { [key: string]: string } = {
      'super_admin': 'bg-red-100 text-red-800',
      'admin': 'bg-blue-100 text-blue-800',
      'quotation_staff': 'bg-blue-100 text-blue-800',
      'viewer': 'bg-yellow-100 text-yellow-800',
      'user': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const handleMyRolesClick = () => {
    if (onNavigateToRoles) {
      onNavigateToRoles();
    }
  };

  // The loading state is a row inside the table rather than an early return, so the title
  // and its actions are on screen from the first paint instead of appearing a beat later.
  return (
    <div className="h-full flex flex-col bg-brand-bg">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 shrink-0">
            <h1 className="text-xl font-semibold leading-7 text-slate-900">User Management</h1>
            <ModuleInfoButton content={USER_MANAGEMENT_INFO_CONTENT} ariaLabel="User Management information" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={handleMyRolesClick}>My roles</Button>
            <Button
              variant="primary"
              onClick={() => {
                setEditingUser(null);
                setIsAddModalOpen(true);
              }}
              icon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Add user
            </Button>
          </div>
        </div>
      </div>

      {/* Users Table. The same card as every other list: a 1px border and no shadow, with the
          page scrolling under a fixed header rather than a 600px box inside it. */}
      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8">
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-auto">
            <table className="data-table min-w-[900px] table-fixed">
              <colgroup>
                <col className="w-[20%]" />
                <col className="w-[26%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead>
                <tr>
                  {['Name', 'Email', 'Created', 'Role', 'Status', 'Actions'].map((heading, i) => (
                    <th key={heading} scope="col" className={i === 5 ? 'text-right' : undefined}>
                      {heading === 'Actions' ? <span className="sr-only">Actions</span> : heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedUsers.visible.map((user) => (
                  <tr key={user.id}>
                    <td className="truncate font-medium text-slate-900" title={`${user.firstName} ${user.lastName}`}>
                      {user.firstName} {user.lastName}
                    </td>
                    <td className="truncate" title={user.email}>
                      {user.email}
                    </td>
                    <td className="whitespace-nowrap">
                      {fmtDate(user.createdAt)}
                    </td>
                    <td className="truncate font-medium text-primary-500">
                      {getRoleDisplayName(user.role)}
                    </td>
                    <td>
                      <StatusBadge active={user.isActive} />
                    </td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditUser(user)}
                          title="Edit user"
                          aria-label={`Edit ${user.firstName} ${user.lastName}`}
                          icon={<Pencil aria-hidden="true" className="h-4 w-4" />}
                        />
                        <ToggleSwitch
                          checked={user.isActive}
                          onChange={() => handleToggleUserStatus(user.id)}
                          label={user.isActive ? `Deactivate ${user.firstName} ${user.lastName}` : `Activate ${user.firstName} ${user.lastName}`}
                        />
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeleteUser(user.id)}
                          title="Delete user"
                          aria-label={`Delete ${user.firstName} ${user.lastName}`}
                          icon={<Trash2 aria-hidden="true" className="h-4 w-4" />}
                        />
                      </div>
                    </td>
                  </tr>
                ))}

                {isLoading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center">
                      <LoadingState text="Loading users..." />
                    </td>
                  </tr>
                )}

                {/* The load error was stored and never shown, so a failed fetch looked
                    exactly like an account with no users in it. */}
                {!isLoading && error && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6">
                      <ErrorState message={error} onRetry={loadUsers} />
                    </td>
                  </tr>
                )}

                {!isLoading && !error && users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6">
                      <EmptyState title="No users yet" detail="Add a user to give someone access to the system." />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination page={pagedUsers.page} pageSize={pagedUsers.pageSize} total={pagedUsers.total} onPageChange={pagedUsers.setPage} noun="users" />
        </div>
      </div>

      {/* Add/Edit User Modal */}
      {isAddModalOpen && (
        <AddUserModal
          user={editingUser}
          roles={roles}
          onClose={() => {
            setIsAddModalOpen(false);
            setEditingUser(null);
          }}
          onSubmit={editingUser ? handleUpdateUser : handleAddUser}
        />
      )}

      {/* Deleting and activating/deactivating both ask through useConfirm: see the handlers. */}

      {/* Error Modal */}
      <ErrorModal
        isOpen={errorModal.isOpen}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
      />
    </div>
  );
};

export default UserManagement;
